import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CanopiLogo } from './CanopiLogo';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

export default function HeroSection() {
  const { t } = useLanguage();
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const textY       = useTransform(scrollYProgress, [0, 0.4],  [0, -60]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.28], [1, 0]);
  const overlayDark = useTransform(scrollYProgress, [0, 0.7],  [0.25, 0.88]);

  return (
    <section id="hero" ref={containerRef} className="relative h-[108vh]" data-testid="hero-section">
      <div className="sticky top-0 h-screen overflow-hidden">

        {/* ── Layer 1: Aerial forest photo ── */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/hero-forest.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* ── Layer 2: Dark atmospheric overlay over photo ── */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 90% 55% at 50% -5%, rgba(8,20,8,0.55) 0%, transparent 65%),
              radial-gradient(ellipse 100% 40% at 50% 100%, rgba(3,6,3,0.85) 0%, transparent 70%),
              linear-gradient(to bottom, rgba(8,14,8,0.45) 0%, rgba(4,7,4,0.30) 40%, rgba(3,5,3,0.65) 100%)
            `,
          }}
        />

        {/* ── Layer 3: Slow-breathing radial glow ── */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ animation: 'hero-breathe 8s ease-in-out infinite' }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-10%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '70%',
              height: '60%',
              background: 'radial-gradient(ellipse at center, rgba(25,55,20,0.22) 0%, transparent 70%)',
              borderRadius: '50%',
            }}
          />
        </div>

        {/* ── Layer 3: Scroll-driven dark overlay ── */}
        <motion.div
          className="absolute inset-0 bg-[#030603]"
          style={{ opacity: overlayDark }}
        />

        {/* ── Layer 4: Edge vignette ── */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 70% at 50% 50%, transparent 30%, rgba(3,6,3,0.55) 100%)',
          }}
        />

        {/* ── Layer 5: Content ── */}
        <motion.div
          className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center"
          style={{ y: textY, opacity: textOpacity }}
        >
          {/* Logo mark */}
          <motion.div
            initial={{ opacity: 0, scale: 0.75 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          >
            <CanopiLogo
              size={60}
              className="text-white mx-auto mb-5 animate-float"
              style={{ filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.3))' }}
            />
          </motion.div>

          {/* Brand name */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.9 }}
            className="mb-7"
          >
            <h1
              className="text-6xl sm:text-7xl lg:text-8xl font-extralight tracking-[0.28em] uppercase text-white"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                textShadow: '0 2px 40px rgba(0,0,0,0.8)',
              }}
            >
              Canopi
            </h1>
          </motion.div>

          {/* Tagline */}
          <motion.p
            className="text-base sm:text-lg lg:text-xl font-light text-white max-w-2xl leading-relaxed"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.9 }}
            style={{ textShadow: '0 1px 20px rgba(0,0,0,0.7)' }}
          >
            {t('hero.tagline')}
          </motion.p>

          {/* Subtitle */}
          <motion.p
            className="mt-4 text-sm sm:text-base text-white/70 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            style={{ textShadow: '0 1px 12px rgba(0,0,0,0.6)' }}
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* CTAs */}
          <motion.div
            className="mt-9 flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
          >
            <a
              href="#interest"
              data-testid="hero-pilot-button"
              className="bg-[#3D5638] hover:bg-[#4A6845] text-white rounded-full px-8 py-4 font-medium tracking-wide transition-all duration-300 shadow-[0_0_24px_rgba(61,86,56,0.5)] hover:shadow-[0_0_36px_rgba(61,86,56,0.65)]"
            >
              {t('hero.ctaPilot')}
            </a>
            <a
              href="#interest"
              data-testid="hero-waitlist-button"
              className="bg-black/30 backdrop-blur-sm border border-white/30 hover:border-white/60 text-white rounded-full px-8 py-4 font-medium transition-all duration-300"
            >
              {t('hero.ctaWaitlist')}
            </a>
          </motion.div>

          {/* Status pill */}
          <motion.div
            className="mt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 text-[0.65rem] font-mono tracking-[0.16em] uppercase text-white/50 border border-white/12 rounded-full px-4 py-1.5 bg-black/20 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#BDD6B5] animate-bio-pulse" />
              {t('hero.overline')}
            </span>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7 }}
          style={{ opacity: textOpacity }}
        >
          <ChevronDown className="text-white/40 animate-bounce" size={26} />
        </motion.div>
      </div>
    </section>
  );
}
